import React, { useEffect, useState, Suspense, lazy } from 'react';
import './App.css';
import { toast } from 'sonner';
import { useAuth } from './auth';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Toaster } from './components/ui/sonner';
import { Skeleton } from './components/ui/skeleton';
import { ScanLine, History as HistoryIcon, Tags, Repeat } from 'lucide-react';
import { LanguageProvider, useLang } from './i18n';
import { ConsentProvider } from './consent';
import { AnalyticsGate } from './analytics';
import { Header } from './components/Header';
import { GenerateTab } from './components/GenerateTab';
import { Footer } from './components/Footer';
import { CookieConsent } from './components/CookieConsent';
import { LegalDialog } from './components/LegalDialog';
import { ContactDialog } from './components/ContactDialog';
import { ReviewsProvider } from './reviews';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AdUnit } from './components/AdUnit';
import { PopularGenerators } from './components/PopularGenerators';
import { GuideTeasers } from './components/GuideTeasers';
import { BookPromoBanner } from './components/BookPromoBanner';
import { SupporterBanner } from './components/SupporterBanner';
import { SideNav } from './components/SideNav';
import { ReviewSection } from './components/ReviewSection';
import { ThemeProvider, useTheme } from './theme';
import { AuthProvider } from './auth';
import { AuthCallback, AuthDialog, AuthRequired, UsernamePrompt } from './components/AuthGate';
import { PaymentReturn } from './components/Pricing';
import api from './api';

// Non-default tabs are code-split: their JS (incl. heavy libs like xlsx) loads
// only when the user first opens that tab, keeping the initial bundle small.
const LabelsTab = lazy(() => import('./components/LabelsTab').then((m) => ({ default: m.LabelsTab })));
const HistoryTab = lazy(() => import('./components/HistoryTab').then((m) => ({ default: m.HistoryTab })));
const ConverterTab = lazy(() => import('./components/ConverterTab').then((m) => ({ default: m.ConverterTab })));
const AdminPage = lazy(() => import('./components/AdminPage'));
const BookLandingPage = lazy(() => import('./components/BookLandingPage'));
const SupporterPage = lazy(() => import('./components/SupporterPage'));

const TabFallback = () => (
  <div className="space-y-4" data-testid="tab-loading">
    <Skeleton className="h-10 w-full" />
    <Skeleton className="h-64 w-full" />
  </div>
);

// Note: the previous in-app aggregateRating JSON-LD was removed together with
// the internal review widget. Public reviews now live on Trustpilot, so we no
// longer emit an internal AggregateRating (keeps structured data honest).

function AppShell() {
  const { t, lang } = useLang();
  const { resolved } = useTheme();
  const [types, setTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [historyKey, setHistoryKey] = useState(0);
  const [tab, setTab] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search).get('tab');
      return ['generate', 'labels', 'history', 'convert'].includes(q) ? q : 'generate';
    } catch (e) {
      return 'generate';
    }
  });
  const [converterMode, setConverterMode] = useState('image-to-barcode');
  const [legalDoc, setLegalDoc] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  const scrollToTabs = () => {
    const el = document.getElementById('generators');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const openConverter = (mode) => { setConverterMode(mode); setTab('convert'); setNavOpen(false); setTimeout(scrollToTabs, 60); };
  const goTab = (t) => { setTab(t); setNavOpen(false); setTimeout(scrollToTabs, 60); };

  // Signal to (headless) prerenderers that the app has painted meaningful
  // content, so they can capture the fully rendered HTML instead of timing out.
  useEffect(() => {
    let id;
    try {
      id = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          window.prerenderReady = true;
        });
      });
    } catch (e) {
      window.prerenderReady = true;
    }
    return () => {
      try { window.cancelAnimationFrame(id); } catch (e) { /* noop */ }
    };
  }, []);


  // Auto-open the contact form when linked from static pages (?contact=1)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('contact') === '1') {
        setContactOpen(true);
        params.delete('contact');
        const qs = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
      }
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    let mounted = true;
    api.getTypes()
      .then((res) => { if (mounted) setTypes(res.data.types || []); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoadingTypes(false); });
    return () => { mounted = false; };
  }, []);

  const refreshHistory = () => setHistoryKey((k) => k + 1);

  return (
    <div className="App flex min-h-screen flex-col">
      <a
        href="#main-content"
        data-testid="skip-to-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[300] focus:rounded-lg focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-primary-foreground focus:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {lang === 'en' ? 'Skip to main content' : 'Zum Hauptinhalt springen'}
      </a>
      <Header onMenuClick={() => setNavOpen(true)} />
      <SideNav
        open={navOpen}
        onClose={() => setNavOpen(false)}
        onOpen={() => setNavOpen(true)}
        activeTab={tab}
        converterMode={converterMode}
        onOpenContact={() => { setNavOpen(false); setContactOpen(true); }}
        onOpenConverter={openConverter}
        onGoTab={goTab}
        onScrollToGenerators={() => { setNavOpen(false); scrollToTabs(); }}
      />
      <main id="main-content" tabIndex={-1} className="mx-auto w-full min-w-0 max-w-5xl flex-1 px-4 py-6 sm:px-6 sm:py-8 lg:px-8 focus:outline-none">
        {/* Supporter banner — above the book tip */}
        <SupporterBanner className="mb-6" />
        {/* Book promo (compact) — top of page for maximum visibility */}
        <BookPromoBanner className="mb-6" variant="compact" />
        {/* Top ad (Google AdSense) — slim horizontal banner. Collapses when unfilled. */}
        <AdUnit slot="9899670158" format="horizontal" responsive testId="ad-unit-top" label={t('ad_label')} className="mx-auto mb-6 w-full max-w-4xl" />

        {/* Popular generators — placed directly under the first ad banner
            (visible internal links for users + SEO). */}
        <div id="generators" className="scroll-mt-28">
          <PopularGenerators className="mb-8" />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <div className="mb-6 flex justify-center">
            <TabsList className="h-auto flex-wrap gap-1 rounded-xl bg-secondary/70 p-1 shadow-sm">
              <TabsTrigger value="generate" data-testid="generate-tab" className="gap-2 rounded-lg px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm">
                <ScanLine className="h-4 w-4" />{t('tab_generate')}
              </TabsTrigger>
              <TabsTrigger value="labels" data-testid="labels-tab" className="gap-2 rounded-lg px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm">
                <Tags className="h-4 w-4" />{t('tab_labels')}
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="history-tab" className="gap-2 rounded-lg px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm" onClick={refreshHistory}>
                <HistoryIcon className="h-4 w-4" />{t('tab_history')}
              </TabsTrigger>
              <TabsTrigger value="convert" data-testid="convert-tab" className="gap-2 rounded-lg px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm">
                <Repeat className="h-4 w-4" />{lang === 'en' ? 'Converter' : 'Konverter'}
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="mb-5 text-center" data-testid="section-intro">
            <h2 className="font-heading text-xl font-semibold text-foreground sm:text-2xl">
              {t(tab === 'generate' ? 'tab_generate' : tab === 'labels' ? 'tab_labels' : 'tab_history')}
            </h2>
            <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
              {t(tab === 'generate' ? 'gen_subtitle' : tab === 'labels' ? 'labels_subtitle' : 'history_subtitle')}
            </p>
          </div>

          {/* Middle ad (Google AdSense). Collapses when unfilled. */}
          <AdUnit
            slot="1381413701"
            format="fluid"
            layout="in-article"
            responsive={false}
            style={{ display: 'block', textAlign: 'center' }}
            testId="ad-unit-middle"
            label={t('ad_label')}
            className="mx-auto mb-6 w-full max-w-3xl"
          />

          {!loadingTypes && types.length > 0 && (
            <>
              <TabsContent value="generate" className="mt-0">
                <GenerateTab types={types} lang={lang} onSaved={refreshHistory} />
              </TabsContent>
              <TabsContent value="labels" className="mt-0">
                  <AuthRequired
                    title={lang === 'en' ? 'Sign in for label sheets' : 'Zum Etiketten-Erstellen anmelden'}
                    description={lang === 'en' ? 'Lay out barcodes on printable A4 label sheets — free account required.' : 'Platziere Barcodes auf druckbaren A4-Etikettenbögen – kostenloses Konto nötig.'}
                  >
                    <Suspense fallback={<TabFallback />}>
                      <LabelsTab types={types} lang={lang} />
                    </Suspense>
                  </AuthRequired>
              </TabsContent>
              <TabsContent value="history" className="mt-0">
                <Suspense fallback={<TabFallback />}>
                  <HistoryTab refreshKey={historyKey} types={types} lang={lang} />
                </Suspense>
              </TabsContent>
              <TabsContent value="convert" className="mt-0">
                <Suspense fallback={<TabFallback />}>
                  <ConverterTab mode={converterMode} onModeChange={setConverterMode} types={types} lang={lang} />
                </Suspense>
              </TabsContent>
            </>
          )}
        </Tabs>

        {/* Combined on-site + Trustpilot ratings (single blended score) */}
        <ReviewSection />

        {/* Guide teasers -> internal linking into the Ratgeber */}
        <GuideTeasers className="mt-12" />

        {/* Book promo banner -> external order links for "Die Wolkenpost von Funkelmoor" */}
        <BookPromoBanner className="mt-12" />

        {/* Bottom ad (Google AdSense). Collapses when unfilled. */}
        <AdUnit slot="9635946273" format="autorelaxed" responsive={false} testId="ad-unit-bottom" label={t('ad_label')} className="mx-auto mt-10 w-full max-w-4xl" />
      </main>
      <Footer onOpenLegal={setLegalDoc} onOpenContact={() => setContactOpen(true)} />
      <CookieConsent onOpenLegal={setLegalDoc} />
      <LegalDialog docKey={legalDoc} onClose={() => setLegalDoc(null)} />
      <ContactDialog open={contactOpen} onClose={() => setContactOpen(false)} />
      <Toaster position="top-center" richColors theme={resolved} />
    </div>
  );
}

function AdminShell() {
  const { resolved } = useTheme();
  return (
    <>
      <Suspense fallback={<div className="flex min-h-screen items-center justify-center text-muted-foreground">Laden…</div>}>
        <AdminPage />
      </Suspense>
      <Toaster position="top-center" richColors theme={resolved} />
    </>
  );
}

function BookShell() {
  const { resolved } = useTheme();
  return (
    <>
      <Suspense fallback={<div className="flex min-h-screen items-center justify-center text-muted-foreground">Laden…</div>}>
        <BookLandingPage />
      </Suspense>
      <Toaster position="top-center" richColors theme={resolved} />
    </>
  );
}

function SupporterShell() {
  const { resolved } = useTheme();
  return (
    <>
      <Suspense fallback={<div className="flex min-h-screen items-center justify-center text-muted-foreground">Laden…</div>}>
        <SupporterPage />
      </Suspense>
      <Toaster position="top-center" richColors theme={resolved} />
    </>
  );
}

// The app is publicly viewable. Login is required only for productive actions
// (generate/export/download/print/save) and for the Batch/Labels tools. The
// admin area is a separate, password-gated shell. This also handles the
// Google OAuth callback fragment on return from the auth provider.
function SessionExpiryNotice() {
  const { sessionExpired, ackSessionExpired, promptLogin } = useAuth();
  const { lang } = useLang();
  const en = lang === 'en';
  useEffect(() => {
    if (!sessionExpired) return;
    toast.info(
      en ? 'You were signed out after a longer break' : 'Du wurdest nach längerer Pause abgemeldet',
      {
        description: en
          ? 'For your security, please sign in again to continue.'
          : 'Zu deiner Sicherheit melde dich bitte erneut an, um fortzufahren.',
        duration: 9000,
        action: {
          label: en ? 'Sign in' : 'Anmelden',
          onClick: () => promptLogin(),
        },
      },
    );
    ackSessionExpired();
  }, [sessionExpired, ackSessionExpired, promptLogin, en]);
  return null;
}

function AppGate() {
  const { resolved } = useTheme();
  const hasSession = typeof window !== 'undefined'
    && window.location.hash
    && window.location.hash.includes('session_id=');
  const isPaymentReturn = typeof window !== 'undefined'
    && (window.location.pathname || '').startsWith('/payment/');

  if (isPaymentReturn) {
    return (
      <>
        <PaymentReturn />
        <Toaster position="top-center" richColors theme={resolved} />
      </>
    );
  }
  if (hasSession) {
    return (
      <>
        <AuthCallback />
        <Toaster position="top-center" richColors theme={resolved} />
      </>
    );
  }
  return (
    <ReviewsProvider>
      <AppShell />
      <AuthDialog />
      <UsernamePrompt />
      <SessionExpiryNotice />
    </ReviewsProvider>
  );
}

export default function App() {
  // Admin login detection. In production the SPA is served as static files, and
  // a deep nested route like /admin/panel can fail to fall back to index.html
  // because a static /admin/index.html directory exists. To make the admin
  // login reliably reachable on ANY static host, we also accept the root URL
  // with an ?admin=1 query param (root always serves the SPA) and a top-level
  // /admin-login path. /admin/panel is kept for backward compatibility.
  let isAdmin = false;
  let isBook = false;
  let isSupporter = false;
  if (typeof window !== 'undefined') {
    const path = window.location.pathname.replace(/\/+$/, '');
    const params = new URLSearchParams(window.location.search);
    isAdmin = path === '/admin/panel'
      || path === '/admin-login'
      || params.get('admin') === '1';
    // Dedicated book landing page — reachable via /buch or root + ?buch=1
    // (root always serves the SPA, so this works on any static host).
    isBook = path === '/buch' || params.get('buch') === '1';
    // Supporter / donation page — reachable via /supporter or root + ?supporter=1.
    isSupporter = path === '/supporter' || params.get('supporter') === '1';
  }
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <ConsentProvider>
            <AnalyticsGate />
            <AuthProvider>
              {isAdmin ? <AdminShell /> : isBook ? <BookShell /> : isSupporter ? <SupporterShell /> : <AppGate />}
            </AuthProvider>
          </ConsentProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
